import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "../../validator";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { ChevronLeft, LoaderCircle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { resentOTP, verifyOTP } from "@/http";
import useCurrentUserStore from "@/store";
import useSonner from "@/hooks/use-sonner";

type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: VoidFunction;
  backTrigger: VoidFunction;
};

const Step2Content: React.FC<PropTypes> = (props) => {
  const { addToast } = useSonner();
  const { setDashboardAccessToken, setCurrentUser } = useCurrentUserStore();
  const email = props.form.watch("step1.email");
  const step2Data = props.form.watch("step2");
  const verifyOtp = useMutation({
    mutationFn: verifyOTP,
    onSuccess: (res) => {
      setDashboardAccessToken(res.data.data.result.access_token);
      setCurrentUser(res.data.data.result);
      addToast({
        message: res.data.data.message || "OTP verified successfully",
        type: "success",
      });

      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "otp_verified",
      });

      props.form.clearErrors();
      props.nextTrigger();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong !",
        type: "error",
      });
    },
  });
  const resendOtp = useMutation({
    mutationFn: resentOTP,
    onSuccess: (res) => {
      addToast({
        message: res.data.data.message || "OTP has been resent successfully.",
        type: "success",
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong !",
        type: "error",
      });
    },
  });
  const handleNextClick = () => {
    const resolve = clientOnboardingSchema.shape.step2.safeParse(step2Data);
    if (resolve.success) {
      verifyOtp.mutate({
        email,
        otp: step2Data.otp,
      });
    }
  };

  useEffect(() => {
    if (step2Data.otp.length === 6) {
      handleNextClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step2Data.otp]);

  return (
    <div className="grid grid-cols-[10%_90%] mt-10 lg:mt-0">
      <ChevronLeft
        className="mt-1 mr-2 cursor-pointer"
        onClick={props.backTrigger}
      />
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3">
          <h2 className="font-semibold text-[28px]/[32px]">
            Enter Verification Code
          </h2>
          <p className="text-sm text-muted-foreground">
            We have sent a code to <b>{email}</b>
          </p>
        </div>

        {/* OTP */}
        <FormField
          control={props.form.control}
          name="step2.otp"
          defaultValue={step2Data.otp}
          render={({ field }) => (
            <FormItem>
              <FormControl className="">
                <InputOTP
                  maxLength={6}
                  {...field}
                  containerClassName="grid grid-cols-[45%_10%_45%] text-center h-12"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator className="flex justify-center" />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* next button */}
        <Button
          type="submit"
          disabled={verifyOtp.isPending}
          className="w-full font-normal"
          onClick={handleNextClick}
        >
          {verifyOtp.isPending && <LoaderCircle className="animate-spin" />}
          Verify
        </Button>

        <p className="text-center">Didn't receive code?</p>
        <Button
          variant="ghost"
          type="button"
          disabled={resendOtp.isPending || verifyOtp.isPending}
          onClick={() => {
            props.form.resetField("step2.otp");
            return resendOtp.mutate({ email });
          }}
          className="p-0 w-full font-bold underline text-base"
        >
          {resendOtp.isPending && <LoaderCircle className="animate-spin" />}
          Resend Code
        </Button>
      </div>
    </div>
  );
};

export default Step2Content;
